import { motion } from "framer-motion";

/**
 * Chapter divider — renders differently per template variant to match each
 * template's typographic personality.
 *
 *  editorial  →  Centered serif chapter, optional intro paragraph, generous py.
 *  mosaic     →  Sticky-ish low-noise label, doesn't interrupt the grid.
 *  cinema     →  Full-width dark interstitial with thin accent rule.
 */
export default function ChapterDivider({ chapter, variant = "editorial", index = 0 }) {
  if (!chapter) return null;

  if (variant === "mosaic") {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="col-span-full flex items-center gap-4 my-6 px-2"
        data-testid={`chapter-${chapter.id}`}
      >
        <div className="text-[10px] tracking-[0.32em] uppercase text-stone-500 shrink-0">
          Chapter {String(index + 1).padStart(2, "0")}
        </div>
        <div className="flex-1 h-px bg-stone-200" />
        <div
          className="font-light text-2xl tracking-tight text-stone-900"
          style={{ fontFamily: "var(--gallery-font-serif)" }}
        >
          {chapter.title}
        </div>
      </motion.div>
    );
  }

  if (variant === "cinema") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-150px" }}
        transition={{ duration: 1, ease: [0.2, 0.65, 0.3, 0.9] }}
        className="py-32 sm:py-40 text-center text-white"
        data-testid={`chapter-${chapter.id}`}
      >
        <div className="text-[10px] tracking-[0.5em] uppercase mb-6 opacity-60">
          Chapter {String(index + 1).padStart(2, "0")}
        </div>
        <h2
          className="font-light text-5xl sm:text-7xl tracking-tight"
          style={{ fontFamily: "var(--gallery-font-serif)" }}
        >
          {chapter.title}
        </h2>
        {chapter.intro && (
          <p className="mt-8 max-w-xl mx-auto text-sm sm:text-base leading-relaxed opacity-70 px-6">
            {chapter.intro}
          </p>
        )}
        <div
          className="h-px w-16 mx-auto mt-10"
          style={{ backgroundColor: "var(--gallery-accent)" }}
        />
      </motion.div>
    );
  }

  // editorial (default)
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="py-20 sm:py-28 text-center"
      data-testid={`chapter-${chapter.id}`}
    >
      <div className="text-[10px] tracking-[0.42em] uppercase text-stone-500 mb-5">
        Chapter {String(index + 1).padStart(2, "0")}
      </div>
      <h2
        className="font-light text-4xl sm:text-6xl tracking-tight text-stone-900"
        style={{ fontFamily: "var(--gallery-font-serif)" }}
      >
        {chapter.title}
      </h2>
      {chapter.intro && (
        <p
          className="mt-7 max-w-xl mx-auto text-sm sm:text-base leading-relaxed text-stone-600 px-6"
          style={{ fontFamily: "var(--gallery-font-sans)" }}
        >
          {chapter.intro}
        </p>
      )}
      <div
        className="h-px w-12 mx-auto mt-8"
        style={{ backgroundColor: "var(--gallery-accent)" }}
      />
    </motion.div>
  );
}
